import date from "date-and-time";
import { formatMonthlyUsd } from "../lib/pricing";

// General Info
export const COMPANY_NAME = "Junoware";
export const FULL_COMPANY_NAME = "Junoware LLC";
export const APP_TYPE = process.env.REACT_APP_TYPE || "operator";
export const APP_NAME = "Clumse";
/** Orange badge next to logo (NavBar + marketing home nav); keep in sync visually. */
export const BRAND_NAV_TAGLINE_BADGE = "Solving clumsy problems";
/**
 * Dispatched on `window` when the global industrial nav opens retail intake on `/`
 * so `Home` can run `openIntake` without a full navigation.
 */
export const CLUMSE_OPEN_RETAIL_INTAKE_EVENT = "clumse-open-retail-intake";
export const APP_ACTION = APP_NAME;
export const APP_NAME_LOWER = APP_NAME.toLowerCase().replace(/\s+/g, "-");

/**
 * Brand hierarchy (customer-facing SEO and copy):
 * - Clumse: company / app name.
 * - Clumse AI: in-app assistant for operators (vendor bids, bid search workflows).
 */
/** Canonical sender label for assistant / Clumse AI turns in chat UIs. */
export const CLUMSE_AI_DISPLAY_NAME = "Clumse AI";
/** Social / OG headline line - operational clarity. */
export const OPERATIONAL_CLARITY_TAGLINE = "Operational Clarity";
/** Product tagline: titles, manifest, and secondary pages. */
export const TAGLINE = "Operational Clarity for Clumsy Operations";
/** Product promise in one sentence (meta, FAQ, schema). */
export const TAGLINE_SENTENCE =
  "Vendor bids and fair pricing for maintenance and repairs - we find and vet local pros, deliver ready-to-bid project data, and help you and contractors align on scope and cost upfront.";
/**
 * Hero eyebrow (above H1) on marketing pages that reuse these tokens.
 */
export const HERO_EYEBROW =
  "SIMPLE FLAT FEES · FIND THE RIGHT PRO · PAY THE RIGHT PRICE";
/**
 * Hero subcopy for pages that import HERO_SUBTITLE (e.g. ForOperators).
 */
export const HERO_SUBTITLE =
  "The savings belong to you. We just charge a simple, one-time fee to find the right pro and lock in the right price. No hidden percentages, no markups on labor.";
/**
 * Default meta / JSON-LD description for routes that do not set a custom snippet.
 * Plain language + brand: who it is for, what you get, where we are from.
 */
export const SEO_DESCRIPTION = `${APP_NAME}. ${TAGLINE_SENTENCE} St. Louis-built for owners and operators nationwide - ZIP-tuned benchmarks, organized bid threads, and Clumse AI until you pick the pro you trust.`;
/** Full app title for meta and browser tabs (non-home routes). */
export const APP_TITLE = `${APP_NAME} | ${BRAND_NAV_TAGLINE_BADGE} · Fair bids & vetted pros`;

/** Homepage document title (primary meta title): B2C homeowner positioning. */
export const HOME_PAGE_TITLE = "Clumse | Home Maintenance, Automated.";
/** Hero headline lines (keep in sync with HomeMain hero + NavFooter tagline). */
export const HOME_HERO_TAGLINE_LINES = ["HOME MAINTENANCE,", "AUTOMATED."];
/** Primary hero / product description (viewport + meta alignment). */
export const HOME_HERO_DESCRIPTION =
  "The easiest way for homeowners to manage repairs and remodeling. Describe your project to our AI, instantly generate a structured job scope, and get accurate bids from vetted local contractors ready to work. Zero guesswork, no wasted time.";
/** Homepage meta name=description (search snippet). */
export const HOME_META_DESCRIPTION = HOME_HERO_DESCRIPTION;
/** Open Graph / Facebook card title. */
export const HOME_OG_TITLE = HOME_PAGE_TITLE;
/** Open Graph card description. */
export const HOME_OG_DESCRIPTION = HOME_HERO_DESCRIPTION;
/** Twitter card description (social preview; aligned with homepage value). */
export const HOME_TWITTER_DESCRIPTION = HOME_HERO_DESCRIPTION;
/** OG image alt: high-intent phrases for accessibility and relevance. */
export const HOME_OG_IMAGE_ALT =
  "Clumse: home maintenance automated — AI job scope, vetted local contractors, and accurate repair bids for homeowners.";
/** Homepage `meta name=keywords` (also mirror roughly in `public/index.html` for pre-JS crawlers). */
export const HOME_META_KEYWORDS =
  "Clumse, vetted contractors, contractor bids, vendor bid search, rental property repairs, ZIP benchmarks, Clumse AI, fair pricing, St. Louis";
/** Homepage wedge monthly price used in marketing comparisons. */
const _homepageWedgeMonthlyUsd = 59;
export const HOME_WEDGE_CLUMSE_AI_MONTHLY_USD = _homepageWedgeMonthlyUsd;
/** @deprecated */
export const OPS_CHAT_MONTHLY_USD = _homepageWedgeMonthlyUsd;
/** @deprecated */
export const OPS_CHAT_PRICE_DISPLAY = `$${formatMonthlyUsd(
  _homepageWedgeMonthlyUsd
)}/mo`;
export const HOMEPAGE_WEDGE_PRICE_DISPLAY = OPS_CHAT_PRICE_DISPLAY;

/**
 * Max doors with occupant relay (Clumse AI) enabled at once on entry tiers.
 */
export const MAX_CONCURRENT_OCCUPANT_RELAYS = 3;
/** @deprecated */
export const OPS_CHAT_MAX_CONCURRENT_OCCUPANT_CHATS = MAX_CONCURRENT_OCCUPANT_RELAYS;

/** @deprecated use HOME_WEDGE_CLUMSE_AI_MONTHLY_USD */
export const HOME_WEDGE_OPSCHAT_MONTHLY_USD = _homepageWedgeMonthlyUsd;

/** Home page meta description; matches HOME_META_DESCRIPTION for Helmet. */
export const SEO_HOME_DESCRIPTION = HOME_META_DESCRIPTION;

export const APP_LOGO = "/logo.png";
export const APP_LOGO_TEXT = "/logo.png";

/**
 * Vetted priority placement subscription after approval (Stripe checkout).
 * Keep in sync with GET /api/vendor/priority-benefits → pricing.monthlyFee.
 */
export const VENDOR_PRIORITY_PLACEMENT_MONTHLY_DISPLAY = "$99/month";

// URL Info
export const APP_URL = "clumse.com";
/** Canonical HTTPS origin for JSON-LD, sitemaps, and AI-facing site summaries (no trailing slash). */
export const SITE_ORIGIN = "https://clumse.com";

/**
 * Bump `OG_IMAGE_CACHE_VERSION` when replacing `public/meta.png`. Facebook, LinkedIn, and
 * X cache preview images by full URL; changing the file only is often not enough until you
 * also change the query string (or use their "scrape again" tools).
 */
export const OG_IMAGE_CACHE_VERSION = "3";
/** Default Open Graph / Twitter image (absolute URL). */
export const DEFAULT_OG_IMAGE_URL = `${SITE_ORIGIN}/meta.png?v=${OG_IMAGE_CACHE_VERSION}`;

// Email Info
export const APP_SUPPORT_EMAIL = `contact@${APP_URL}`;

// Service Info
export const PERSONA_TERM = "operator";
export const adminUserId = "kKDOmz9HyQW6XwaufxZkvmnNeF02";

export const galleryPageLimit = 6;
export const commentLengthThreshold = 1;
export const commentLengthMax = 500;

export const spinnerIcon = "/icons/spinner.svg";

export const defaultArtQuestCard = "";
export const defaultWritingQuestCard = "";
export const defaultVoteCard = "";
export const defaultArtQuestFull = "";
export const defaultWritingQuestFull = "";
export const defaultVoteFull = "";

export const tikTokLink = `https://www.tiktok.com/@${APP_NAME_LOWER}`;
export const instagramLink = `https://www.instagram.com/get${APP_NAME_LOWER}/`;
export const twitterLink = `https://twitter.com/get${APP_NAME_LOWER}`;
export const discordLink = "https://discord.gg/";
export const facebookLink = `https://www.facebook.com/get${APP_NAME_LOWER}`;
export const linkedinLink = `https://www.linkedin.com/company/clumse/ `;

export const datePattern = date.compile("ddd, MMM DD");
export const questCardDatePattern = date.compile("MMMM DD, YYYY");

export const noHeaderUiPaths = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/payment-success",
  "/payment-setup",
  "/pay-rent",
  "/tenant-chat",
  "/access-denied",
  "/token-expired",
  "/communication",
  "/communication/:tenantId",
  "/lease/", // Public listing pages (e.g. /lease/:listingId); trailing slash so /lease-ups is not matched
  "/background-check/success",
  "/background-check/error",
  "/p/",
  "/negotiate",
  /** Footer only: global NavBar hidden, NavFooter (copyright) still shown */
  "/legacy",
  "/preflight",
  "/ceo-pulse",
];

export const noFooterUiPaths = [
  "/chat",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/payment-success",
  "/payment-setup",
  "/pay-rent",
  "/tenant-chat",
  "/access-denied",
  "/token-expired",
  "/communication",
  "/communication/:tenantId",
  "/background-check/success",
  "/background-check/error",
  "/p/",
  "/negotiate",
  "/preflight",
  "/ceo-pulse",
];

export const ERROR_PROFANITY =
  "Oops! Please use kind words. Let's keep our community positive and respectful ✌️";
export const usernameTakenErrorText = "That username is taken";
export const passwordErrorText = "Wrong password";

// The values here are used as keys in the Firebase table. Ye be warned when a-changing them.
export const configs = {
  likeYourComment: "like-your-comment",
  replyYourComment: "reply-your-comment",
  likeYourSubmission: "like-your-submission",
  replyYourSubmission: "reply-your-submission",
  newWebcomic: "new-webcomic",
  newEpisode: "new-episode",
  newQuest: "new-quest",
  questVotingLive: "quest-voting-live",
  resultsAnnounced: "results-announced",
  creatorPollLive: "creator-poll-live",
};

export const LOCAL_STORAGE = {
  hasAlerts: "has-alerts",
};
export const SESSION_STORAGE = {
  redirectUrl: "redirect-url",
};

export const ONE_DAY_FROM_NOW = new Date(
  new Date().getTime() + 60 * 60 * 24 * 1000
);

export const TWO_DAYS_FROM_NOW = new Date(
  new Date().getTime() + 60 * 60 * 48 * 1000
);

export const ALERT_AMOUNT_PER_PAGE = 15;

export const QUEST_TYPES = {
  VISUAL: "visual",
  WRITING: "writing",
  // TODO: remove after ai is gone
  IMAGE: "image",
};

export const SUBMISSION_TYPES = {
  IDEA: "idea",
  TEXT: "text",
  // TODO: remove after ai is gone
  GEN_AI: "image",
};

export const adminList = ["bryan@junoware.com"];

// Debug mode helpers for URL-based debug detection
// Simply checks if ?debug=true is in the URL (or just ?debug)
// Returns true if debug=true, debug=1, or just ?debug (without value)
// Returns false if debug=false, debug=0, or debug parameter is not present
export const getDebugMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);

    // Check if debug parameter exists
    if (!urlParams.has("debug")) {
      return false; // Not present = false
    }

    const debugParam = urlParams.get("debug");

    // Explicitly false
    if (debugParam === "false" || debugParam === "0") {
      return false;
    }

    // Explicitly true, or just ?debug (without value = empty string or null)
    if (
      debugParam === "true" ||
      debugParam === "1" ||
      debugParam === "" ||
      debugParam === null
    ) {
      return true;
    }

    // Any other value = false
    return false;
  } catch (e) {
    return false;
  }
};

/**
 * Shared rule for URL demo mode (`?demo`, `?demo=true`, `?demo=1`; off with `?demo=false` / `?demo=0`).
 * Use from React with `searchParams.has("demo")` and `searchParams.get("demo")`, or from `window`.
 */
export function isActiveDemoQueryParam(hasDemoParam, demoValue) {
  if (!hasDemoParam) return false;
  if (demoValue === "false" || demoValue === "0") return false;
  if (
    demoValue === "true" ||
    demoValue === "1" ||
    demoValue === "" ||
    demoValue === null
  ) {
    return true;
  }
  return false;
}

/** True when ?demo=true, ?demo=1, or ?demo (empty). Used for vendor bid demos (no real vendor email). */
export const getVendorBidDemoMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return isActiveDemoQueryParam(urlParams.has("demo"), urlParams.get("demo"));
  } catch (e) {
    return false;
  }
};

/**
 * Mirrors backend DEMO_MODE for launch / whale-mode UI (dashboard ghost data).
 * Set REACT_APP_DEMO_MODE=true in the frontend env at build time.
 */
export const isLaunchDemoMode = () => {
  if (
    typeof process !== "undefined" &&
    process.env.REACT_APP_DEMO_MODE === "true"
  ) {
    return true;
  }
  return false;
};

/**
 * Set REACT_APP_DEPLOYMENT=sandbox in the build env (e.g. Netlify) for non-prod
 * marketing / preview deploys. Used to gate debug-only UI such as "Clear demo limits"
 * on the public engine chat demo.
 */
export const isSandboxDeployment = () =>
  String(typeof process !== "undefined" ? process.env.REACT_APP_DEPLOYMENT : "")
    .trim()
    .toLowerCase() === "sandbox";

/** Dedicated marketing screenshot route or explicit query flag. */
export function isMarketingSandboxPath(pathname) {
  return String(pathname || "").replace(/\/$/, "") === "/debug/marketing-sandbox";
}

export function isMarketingSandboxQueryActive(searchParams) {
  if (!searchParams?.has?.("marketingSandbox")) return false;
  const v = searchParams.get("marketingSandbox");
  if (v === "false" || v === "0") return false;
  if (v === "true" || v === "1" || v === "" || v === null) return true;
  return false;
}

export function isMarketingSandboxActive(pathname, searchParams) {
  return (
    isMarketingSandboxPath(pathname) ||
    isMarketingSandboxQueryActive(searchParams)
  );
}

/**
 * Floating marketing debug controls — never on production builds unless
 * REACT_APP_DEPLOYMENT=sandbox (preview deploys).
 */
export function isMarketingDebugControlsEnabled(pathname, searchParams) {
  const isProdBuild =
    typeof process !== "undefined" && process.env.NODE_ENV === "production";
  if (isProdBuild && !isSandboxDeployment()) return false;
  if (isMarketingSandboxActive(pathname, searchParams)) return true;
  return getDebugMode();
}

/**
 * With URL debug mode on /demo/bid-comparison, pass ?clean=true (or ?clean or ?clean=1)
 * to hide the preview ribbon and negotiation panel for marketing screenshots.
 */
export const getBidComparisonCleanScreenshot = () => {
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search);
    if (!p.has("clean")) return false;
    const v = p.get("clean");
    if (v === "false" || v === "0") return false;
    if (v === "true" || v === "1" || v === "" || v === null) return true;
    return false;
  } catch (e) {
    return false;
  }
};

// Get debug unit count from URL parameter (defaults to 5)
export const getDebugUnitCount = () => {
  if (typeof window === "undefined") return 5;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const unitsParam = urlParams.get("units");
    if (unitsParam) {
      const count = parseInt(unitsParam, 10);
      // Limit to reasonable range (1-105 units)
      if (!isNaN(count) && count >= 1 && count <= 105) {
        return count;
      }
    }
    return 5; // Default
  } catch (e) {
    return 5;
  }
};

// Settings demo: which part of Settings to show with demo data.
// URL: ?debug=true&settings_demo=accounts | assets | both
// - accounts: show "Your deposit accounts" with multiple demo accounts (Primary + LLCs)
// - assets: show Door settings with multiple doors, some assigned to different deposit accounts
// - both: demo deposit accounts and demo assets with mixed assignments
export const getSettingsDemoMode = () => {
  if (typeof window === "undefined") return null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("settings_demo")) return null;
    const v = (urlParams.get("settings_demo") || "").toLowerCase();
    if (v === "accounts" || v === "assets" || v === "both") return v;
    return null;
  } catch (e) {
    return null;
  }
};

// Get custom name from URL parameter for engine widget personalization
// Usage: ?name=John will personalize the greeting
export const getCustomName = () => {
  if (typeof window === "undefined") return null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const nameParam = urlParams.get("name");
    if (nameParam && nameParam.trim().length > 0) {
      // Return the name with first letter capitalized
      return (
        nameParam.trim().charAt(0).toUpperCase() + nameParam.trim().slice(1)
      );
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const defaultProfileImages = [];

export function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

export const generalQuillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      {
        color: [
          "#FFF6F2",
          "#EC55AB",
          "#F5A2FF",
          "#F6B438",
          "#EDE590",
          "#3DDE9C",
          "#4DADF9",
          "#7586FF",
        ],
      },
    ],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
  imageCompress: {
    quality: 0.5,
    maxWidth: 10000, // default
    maxHeight: 10000, // default
    // imageType: "image/jpeg", // default
    debug: false, // default
    suppressErrorLogging: false, // default
    insertIntoEditor: undefined, // default
    /* 
 insertIntoEditor: async (imageBase64URL, imageBlob) => {
 const downloadUrl = await uploadImage("questImages", imageBlob);
 console.log(downloadUrl);
 return imageBlob;
 },
 */
  },
  imageResize: {
    modules: ["Resize", "DisplaySize"],
    handleStyles: {
      backgroundColor: "white",
      border: "none",
      color: "white",
    },
  },
};

export const createPollQuillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      {
        color: [
          "#FFF6F2",
          "#EC55AB",
          "#F5A2FF",
          "#F6B438",
          "#EDE590",
          "#3DDE9C",
          "#4DADF9",
          "#7586FF",
        ],
      },
    ],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
  imageCompress: {
    quality: 0.8,
    maxWidth: 1000,
    maxHeight: 1000,
    imageType: "image/jpeg",
    debug: false,
    suppressErrorLogging: false,
    insertIntoEditor: undefined,
  },
  imageResize: {
    modules: ["Resize", "DisplaySize"],
    handleStyles: {
      backgroundColor: "white",
      border: "none",
      color: "white",
    },
  },
};

export const createCreatorPollQuillModules = {
  toolbar: [["bold", "italic", "underline", "emoji", { list: "bullet" }]],
  "emoji-toolbar": true,
  "emoji-shortname": true,
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
};

export const vhPanelLeftPanelWidth = 360;
export const vhPanelrightPanelLeftMargin = 10;

export const storyworldTypes = {
  ARCHIVED: "archived",
  CATALOG: "catalog",
  STORYWORLD: "storyworld",
};

// dropdown is set by antd currently at 1050
export const zIndex = {
  sheet: 999,
  authSheet: 1000,
};

export const maxIdeaBlockEntries = 3;

export const PROMO_STATUS = {
  ACTIVE: "active",
  EXPIRED: "expired",
  SCHEDULED: "scheduled",
};

export const getPromoStatus = (promo) => {
  if (!promo) return "";

  const start = promo.startTimestamp;
  const end = promo.endTimestamp;

  if (start < Date.now() && end > Date.now()) {
    return PROMO_STATUS.ACTIVE;
  } else if (end < Date.now()) {
    return PROMO_STATUS.EXPIRED;
  }

  return PROMO_STATUS.SCHEDULED;
};

export const getPromoStatusColor = (promo) => {
  const status = getPromoStatus(promo);

  if (status === PROMO_STATUS.ACTIVE) {
    return "#70F2BA";
  } else if (status === PROMO_STATUS.EXPIRED) {
    return "var(--color-red-error)";
  }

  return "#D9D9D9";
};

export const MIN_USERNAME_LENGTH = 3;

// Define the available asset instance types for operators
export const unitTypes = [
  {
    value: "apartment",
    label: "Apartment",
    buttonText: "Apartment",
  },
  { value: "house", label: "House", buttonText: "House" },
  { value: "condo", label: "Condo", buttonText: "Condo" },
  {
    value: "townhouse",
    label: "Townhouse",
    buttonText: "Townhouse",
  },
  { value: "duplex", label: "Duplex", buttonText: "Duplex" },
  {
    value: "studio",
    label: "Studio",
    buttonText: "Studio",
  },
  {
    value: "loft",
    label: "Loft",
    buttonText: "Loft",
  },
  {
    value: "mobile-home",
    label: "Mobile Home",
    buttonText: "Mobile Home",
  },
  {
    value: "commercial",
    label: "Commercial",
    buttonText: "Commercial",
  },
  {
    value: "multi-family",
    label: "Multi-Family",
    buttonText: "Multi-Family",
  },
];

// Define US states for lease templates
export const usStates = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];
